import { useState } from 'react';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useTheme } from '@mui/material/styles';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { CopyIcon, RetryIcon } from '../../assets/Icons/Icons';
// import Chip from '@mui/material/Chip';

interface ChatMessageProps {
  message: {
    id: string;
    text: string;
    isUser: boolean;
    timestamp: Date;
    tools?: string[];
    toolsUsed?: string[];
    toolResponses?: any[];
  };
  onFeedback?: (messageId: string, type: 'like' | 'dislike') => void;
  onCopy?: (text: string) => void;
  onRetry?: (messageId: string) => void;
  compact?: boolean;
}

export const ChatMessage = ({
  message,
  onRetry,
  compact = false,
}: ChatMessageProps) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [selectedTool, _setSelectedTool] = useState<string | null>(null);

  const getCardBackgroundColor = () => {
    if (!message.isUser) return 'transparent';
    return isDarkMode
      ? 'var(--xlrt-color-ui-gray40)'
      : 'var(--xlrt-color-ui-gray85)';
  };

  const handleCopyResponse = async () => {
    try {
      await window.navigator.clipboard.writeText(message.text);
      setCopiedText(message.text);
      setTimeout(() => setCopiedText(null), 2000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy text:', err);
    }
  };

  // const handleTooltipToggle = (toolName: string) => {
  //   setSelectedTool(selectedTool === toolName ? null : toolName);
  // };

  const getToolResponseForTool = (toolName: string) => {
    if (!message.toolsUsed || !message.toolResponses) {
      return 'No tools used or no tool responses available';
    }

    const toolResponse = message.toolResponses.find(
      response => response.name === toolName,
    );

    if (!toolResponse) {
      return `No response data found for tool: ${toolName}`;
    }

    return JSON.stringify(toolResponse, null, 2);
  };

  const handleCopyToolResponse = async (toolName: string) => {
    try {
      const toolResponse = getToolResponseForTool(toolName);
      await window.navigator.clipboard.writeText(toolResponse);
      setCopiedText(toolResponse);
      setTimeout(() => setCopiedText(null), 2000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy tool response:', err);
    }
  };

  const CodeBlock = ({ children, ...props }: any) => {
    return (
      <Box sx={{ position: 'relative' }}>
        <pre {...props}>{children}</pre>
      </Box>
    );
  };

  const formatMessage = (text: string) => {
    // Don't render empty or whitespace-only messages as markdown
    if (!text || !text.trim()) {
      return (
        <Typography
          variant="body1"
          sx={{
            fontSize: '0.875rem',
            lineHeight: message.isUser ? 1.5 : 1.6,
            color: theme.palette.text.primary,
            fontWeight: message.isUser ? 500 : 'normal',
            fontFamily: 'inherit',
          }}
        >
          {text}
        </Typography>
      );
    }

    // Check if the message contains markdown-like content
    const hasMarkdown =
      /[#*_`\[\]]/g.test(text) ||
      text.includes('```') ||
      text.includes('\n') ||
      text.includes('|') || // tables
      text.includes('> '); // blockquotes

    if (hasMarkdown) {
      return (
        <Box
          sx={{
            fontFamily: 'inherit',
            lineHeight: '20px',
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              marginTop: theme.spacing(2),
              marginBottom: theme.spacing(1),
              fontWeight: 600,
              fontFamily: 'inherit',
            },
            '& h1': {
              fontSize: '1.5rem',
            },
            '& h2': {
              fontSize: '1.3rem',
            },
            '& h3': {
              fontSize: '1.1rem',
            },
            '& p': {
              margin: theme.spacing(0.5, 0),
              lineHeight: 1.6,
              fontFamily: 'inherit',
            },
            '& ul, & ol': {
              margin: theme.spacing(0.5, 0),
              paddingLeft: theme.spacing(3),
              fontFamily: 'inherit',
            },
            '& li': {
              margin: theme.spacing(0.25, 0),
              fontFamily: 'inherit',
            },
            '& blockquote': {
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              paddingLeft: theme.spacing(2),
              margin: theme.spacing(1, 0),
              fontStyle: 'italic',
              backgroundColor: theme.palette.background.default,
              padding: theme.spacing(1, 1, 1, 2),
              borderRadius: theme.spacing(0.5),
            },
            '& code': {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.text.primary,
              padding: '2px 4px',
              borderRadius: '3px',
              fontFamily: 'inherit',
              fontSize: '0.875em',
            },
            '& pre': {
              backgroundColor: theme.palette.background.default,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: theme.spacing(0.5),
              padding: theme.spacing(1.5),
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              margin: theme.spacing(1, 0),
              overflow: 'auto',
              position: 'relative',
              '& code': {
                backgroundColor: 'transparent',
                padding: 0,
                color: theme.palette.text.primary,
              },
            },
            '& table': {
              borderCollapse: 'collapse',
              width: '100%',
              margin: theme.spacing(1, 0),
            },
            '& th, & td': {
              border: `1px solid ${theme.palette.divider}`,
              padding: theme.spacing(0.5, 1),
              textAlign: 'left',
            },
            '& th': {
              backgroundColor: theme.palette.action.hover,
              fontWeight: 600,
            },
            '& a': {
              color: 'inherit',
              textDecoration: 'underline',
              '&:hover': {
                textDecoration: 'underline',
                color: isDarkMode
                  ? 'var(--xlrt-color-future-green500)'
                  : 'var(--xlrt-color-future-green800)',
              },
            },
            '& hr': {
              border: 'none',
              borderTop: `1px solid ${theme.palette.divider}`,
              margin: theme.spacing(2, 0),
            },
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              pre: CodeBlock,
            }}
          >
            {text}
          </ReactMarkdown>
        </Box>
      );
    }

    // For simple text messages, use Typography with appropriate styling
    return (
      <Typography
        variant="body1"
        sx={{
          fontSize: '0.875rem',
          lineHeight: message.isUser ? 1.5 : 1.6,
          color: theme.palette.text.primary,
          fontWeight: message.isUser ? 500 : 'normal',
          fontFamily: 'inherit',
        }}
      >
        {text}
      </Typography>
    );
  };

  let maxWidth = '100%';
  if (compact) {
    maxWidth = '269px';
  } else if (message.isUser) {
    maxWidth = '70%';
  }

  let messagePadding: string | number = 0;
  if (message.isUser) {
    messagePadding = compact ? '10px 12px' : '10px';
  }

  return (
    <Box
      data-testid="message-container"
      className={message.isUser ? 'user-message' : 'bot-message'}
      {...(!message.isUser && {
        'data-ai-generated': 'true',
        'data-ai-provider': 'xpert',
      })}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: message.isUser ? 'flex-end' : 'flex-start',
        gap: compact ? 0 : theme.spacing(4),
        marginBottom: compact ? '12px' : theme.spacing(3),
        width: '100%',
      }}
    >
      <Box
        sx={{
          maxWidth,
          minWidth: 0,
        }}
      >
        <Card
          sx={{
            maxWidth: '100%',
            position: 'relative',
            backgroundColor: getCardBackgroundColor(),
            color: 'inherit',
            border: 'none',
            borderRadius: message.isUser ? theme.spacing(0.5) : 0,
            boxShadow: 'none',
            '&:hover .message-actions': {
              opacity: 1,
            },
          }}
        >
          <Box
            sx={{
              padding: messagePadding,
              overflowWrap: 'anywhere',
            }}
          >
            {formatMessage(message.text)}

            {/* Show tools section for tools that were used */}
            {(message.toolsUsed || message.tools) &&
              (message.toolsUsed || message.tools)!.length > 0 && (
                <Box
                  sx={{
                    marginTop: theme.spacing(1.5),
                    padding: theme.spacing(1, 0),
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.625rem',
                      marginBottom: theme.spacing(1),
                      color: theme.palette.text.primary,
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Tooltip
                      title={
                        copiedText === message.text
                          ? 'Copied!'
                          : 'Copy response'
                      }
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={handleCopyResponse}
                        sx={{ padding: 0 }}
                        disableRipple
                      >
                        <CopyIcon />
                      </IconButton>
                    </Tooltip>
                    {/* <Typography
                      variant="caption"
                      style={{ fontWeight: 'bold' }}
                    >
                      Tools used ({(message.toolsUsed || message.tools)!.length}
                      )
                    </Typography> */}
                    {/* {(message.toolsUsed || message.tools)!.map(tool => (
                      <Chip
                        key={tool}
                        label={tool}
                        size="small"
                        clickable
                        onClick={() => handleTooltipToggle(tool)}
                        icon={<CopyIcon fontSize="small" />}
                        sx={{
                          height: 24,
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          backgroundColor: 'transparent',
                          color:
                            selectedTool === tool
                              ? theme.palette.primary.main
                              : theme.palette.text.secondary,
                          margin: '0 4px 0 8px',
                          border:
                            selectedTool === tool
                              ? `2px solid ${theme.palette.primary.main}`
                              : `1px solid ${theme.palette.divider}`,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                            color: theme.palette.text.primary,
                            transform: 'translateY(-1px)',
                          },
                        }}
                      />
                    ))} */}
                    <Tooltip title="Retry" arrow>
                      <IconButton
                        size="small"
                        onClick={() => onRetry?.(message.id)}
                        sx={{ padding: 0 }}
                        disableRipple
                      >
                        <RetryIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Tool responses - shown below the chips */}
                  {(message.toolsUsed || message.tools)!.map(tool => (
                    <Collapse
                      key={`collapse-${tool}`}
                      in={selectedTool === tool}
                    >
                      <Card
                        sx={{
                          marginTop: theme.spacing(1),
                          backgroundColor: theme.palette.background.default,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: theme.spacing(1),
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: theme.spacing(1, 1.5),
                            backgroundColor: theme.palette.action.hover,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            borderRadius: theme.spacing(1, 1, 0, 0),
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {tool} Response
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyToolResponse(tool)}
                            title={copiedText ? 'Copied!' : 'Copy response'}
                            sx={{
                              color: theme.palette.text.primary,
                            }}
                          >
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Box
                          sx={{
                            padding: theme.spacing(1.5),
                            maxHeight: '300px',
                            overflow: 'auto',
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: theme.spacing(0.5),
                              padding: theme.spacing(1.5),
                              fontSize: '0.8rem',
                              lineHeight: 1.4,
                              overflow: 'auto',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              color: theme.palette.text.primary,
                            }}
                          >
                            {getToolResponseForTool(tool)}
                          </Box>
                        </Box>
                      </Card>
                    </Collapse>
                  ))}
                </Box>
              )}
          </Box>
        </Card>
      </Box>
    </Box>
  );
};
